import React, {useEffect, useId, useMemo, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Stack, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * ReasoningBox - A collapsible box that displays AI reasoning steps.
 * Shows the last reasoning step by default, with the ability to expand to see all steps.
 * Text is rendered in markdown format.
 */
const ReasoningBox = ({reasoningSteps = [], isActive = false}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const summaryId = useId();
    const detailsId = `${summaryId}-details`;

    const displaySteps = useMemo(() => normalizeSteps(reasoningSteps), [reasoningSteps]);


    const historySteps = isActive ? displaySteps.slice(0, -1) : displaySteps;
    const hasHistorySteps = historySteps.length > 0;
    const currentStep = isActive ? displaySteps[displaySteps.length - 1] : null;

    useEffect(() => {
        if (!hasHistorySteps && isExpanded) {
            setIsExpanded(false);
        }
    }, [hasHistorySteps, isExpanded]);

    const handleAccordionToggle = (_event, expandedState) => {
        if (hasHistorySteps) {
            setIsExpanded(expandedState);
        }
    };

    return (
        <Box component="section" aria-live={isActive ? 'polite' : 'off'}>
            <Accordion
                disableGutters
                square
                elevation={0}
                expanded={hasHistorySteps ? isExpanded : false}
                onChange={handleAccordionToggle}
                sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    backgroundColor: isActive ? 'action.hover' : 'background.paper',
                    transition: (theme) => theme.transitions.create(['border-color', 'background-color']),
                    '&::before': {display: 'none'},
                }}
            >
                <AccordionSummary
                    id={summaryId}
                    aria-controls={hasHistorySteps ? detailsId : undefined}
                    expandIcon={hasHistorySteps ? (
                        <ExpandMoreIcon sx={{color: isActive ? 'primary.main' : 'text.disabled'}}/>
                    ) : null}
                    sx={{
                        px: 2,
                        py: 1.25,
                        minHeight: 'auto',
                        '& .MuiAccordionSummary-content': {
                            m: 0,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        },
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <PsychologyIcon
                            sx={{
                                fontSize: 20,
                                color: isActive ? 'primary.main' : 'text.secondary',
                                animation: isActive ? 'pulse 1.5s infinite' : 'none',
                                '@keyframes pulse': {
                                    '0%, 100%': {opacity: 1},
                                    '50%': {opacity: 0.5},
                                },
                            }}
                        />
                        <Typography variant="subtitle2" sx={{fontWeight: 600}}
                                    color={isActive ? 'primary.main' : 'text.secondary'}>
                            {isActive ? 'Thinking...' : 'Reasoning'}
                        </Typography>
                    </Stack>
                    {hasHistorySteps && (
                        <Typography variant="caption" color="text.secondary">
                            {`${displaySteps.length} step${displaySteps.length > 1 ? 's' : ''}`}
                        </Typography>
                    )}
                </AccordionSummary>

                {hasHistorySteps && (
                    <AccordionDetails id={detailsId} sx={{pt: 0}}
                                      aria-labelledby={summaryId}>
                        <Stack spacing={1.5}>
                            {historySteps.map((step, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            mb: 0.5,
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Step {index + 1}
                                    </Typography>
                                    <ReasoningMarkdown content={step}/>
                                </Box>
                            ))}
                        </Stack>
                    </AccordionDetails>
                )}
            </Accordion>

            {currentStep && (
                <Box
                    sx={{
                        mt: 1.5,
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'primary.light',
                        backgroundColor: 'background.paper',
                    }}
                >
                    <Stack direction={'row'} alignItems="center" spacing={1} justifyContent={'space-between'}>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mb: 0.5,
                                color: 'primary.main',
                                fontWeight: 600,
                            }}
                        >
                            Step {displaySteps.length} (current)
                        </Typography>
                        <CircularProgress size={16}/>
                    </Stack>
                    <ReasoningMarkdown content={currentStep}/>
                </Box>
            )}
        </Box>
    );
};

/**
 * ReasoningMarkdown - Renders markdown content with appropriate styling for reasoning text
 */
const ReasoningMarkdown = ({content}) => {
    if (!content) return null;

    return (
        <Box
            sx={{
                '& > *:first-of-type': {mt: 0},
                '& > *:last-child': {mb: 0},
            }}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({node, ...props}) => (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 1,
                                lineHeight: 1.6,
                                color: 'text.secondary',
                            }}
                            {...props}
                        />
                    ),
                    h1: ({node, ...props}) => (
                        <Typography
                            component="h1"
                            variant="body1"
                            sx={{fontWeight: 700, mb: 1, color: 'text.primary'}}
                            {...props}
                        />
                    ),
                    h2: ({node, ...props}) => (
                        <Typography
                            component="h2"
                            variant="body1"
                            sx={{fontWeight: 700, mb: 1, color: 'text.primary'}}
                            {...props}
                        />
                    ),
                    h3: ({node, ...props}) => (
                        <Typography
                            component="h3"
                            variant="body1"
                            sx={{fontWeight: 700, mb: 1, color: 'text.primary'}}
                            {...props}
                        />
                    ),
                    h4: ({node, ...props}) => (
                        <Typography
                            component="h4"
                            variant="body1"
                            sx={{fontWeight: 700, mb: 1, color: 'text.primary'}}
                            {...props}
                        />
                    ),
                    h5: ({node, ...props}) => (
                        <Typography
                            component="h5"
                            variant="body1"
                            sx={{fontWeight: 700, mb: 1, color: 'text.primary'}}
                            {...props}
                        />
                    ),
                    h6: ({node, ...props}) => (
                        <Typography
                            component="h6"
                            variant="body1"
                            sx={{fontWeight: 700, mb: 1, color: 'text.primary'}}
                            {...props}
                        />
                    ),
                    strong: ({node, ...props}) => (
                        <Box component="strong" sx={{fontWeight: 600, color: 'text.primary'}} {...props} />
                    ),
                    em: ({node, ...props}) => (
                        <Box component="em" sx={{fontStyle: 'italic'}} {...props} />
                    ),
                    ul: ({node, ...props}) => <Box component="ul" sx={{pl: 3, mb: 1.5}} {...props} />,
                    ol: ({node, ...props}) => <Box component="ol" sx={{pl: 3, mb: 1.5}} {...props} />,
                    li: ({node, ...props}) => (
                        <Typography component="li" variant="body2"
                                    sx={{lineHeight: 1.5, color: 'text.secondary'}} {...props} />
                    ),
                }}
            >
                {content}
            </ReactMarkdown>
        </Box>
    );
};

function normalizeSteps(steps) {
    if (!Array.isArray(steps)) return [];
    const trimmed = steps.map(s => (typeof s === 'string' ? s.trim() : '')).filter(Boolean);
    if (trimmed.length === 0) return [];
    if (trimmed.length === 1) {
        const parts = trimmed[0].split(/(?=Step\s+\d+)/i).map(p => p.trim()).filter(Boolean);
        return parts.length > 1 ? parts : trimmed;
    }
    return trimmed;
}

export default ReasoningBox;
